import { api } from "../api/client";

/** Why push cannot be on, in reader terms. "unavailable" hides the control: the server has no
 * VAPID keys yet. "not-installed" is iOS, where push only works from the home-screen app. */
export type PushState = "on" | "off" | "denied" | "unsupported" | "not-installed" | "unavailable";

const supported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const ios = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

const installed = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export async function pushState(serverPush: boolean): Promise<PushState> {
  if (!supported()) return "unsupported";
  if (ios() && !installed()) return "not-installed";
  if (!serverPush) return "unavailable";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription()) ? "on" : "off";
}

function urlB64ToBytes(b64: string): Uint8Array {
  const padded = b64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Subscribe this device and tell the server. Must be called from a user gesture (the browser
 * shows its own permission prompt); throws an Error the caller can toast. */
export async function enablePush(): Promise<void> {
  if (!supported()) throw new Error("This browser cannot show alerts. The daily email digest still works.");
  if (ios() && !installed())
    throw new Error("On iPhone, add Learn to your Home Screen first, then turn on alerts.");
  const { public_key } = await api.pushKey();
  if (!public_key) throw new Error("Push alerts are not set up on this server yet.");
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToBytes(public_key) as BufferSource,
  });
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth)
    throw new Error("The browser did not return a usable push subscription.");
  await api.savePush({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
}

export async function disablePush(): Promise<void> {
  if (!supported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.deletePush(sub.endpoint);
  } finally {
    await sub.unsubscribe();
  }
}
